import React, { useState, useEffect, useCallback, useRef } from "react";
import { Save, Printer, Loader, View } from "lucide-react";
import { Box, IconButton, Tab, ThemeProvider, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { HelpOutline } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import TimelineTab from "../salesInvoice/TimelineTab";
import ModeIndicator from "../salesInvoice/ModeIndicator";
import {
  DOCUMENT_TYPES,
  getDocumentConfig,
  fetchDocumentNumber,
  MODIFICATION_TYPES,
  tooltipContent,
  theme,
} from "../config/DocumentConfig";
import { purchaseData } from "../salesInvoice/data";
import { getTodayDate } from "../../utils/constants";
import { calculateRowValues } from "../config/PurchaseInvoiceTableConfig";
import Modal from "../common/CommonModal";
import ModificationCommentModal from "../salesInvoice/ModificationCommentModal";
import ProductionGeneral from "./ProductionGeneral";
import ProductionTable from "./ProductionTable";
import checkIfUserHasAccess from "../common/UserCheckAccess";
import FloatingItemInformation from "../salesInvoice/FloatingItemInformation";

const Production = ({ documentType = DOCUMENT_TYPES.PRODUCTION }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useApi();
  const config = getDocumentConfig(documentType);

  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const CURRENT_USER = localStorage?.getItem("currentUserName") || "admin";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const todayDate = getTodayDate();
  const deptLocation = localStorage.getItem("departmentLocation") || "A";

  const documentNoFromState = location?.state?.docNo || null;
  const modeFromState = location?.state?.mode || "new";
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState(modeFromState);

  const [parentTabValue, setParentTabValue] = useState("1");

  const [autoVatIsOn, setAutoVatIsOn] = useState(true);
  const [vatAmountManual, setVatAmountManual] = useState(null);
  const [discountAmount, setDiscountAmount] = useState("");
  const [roundOff, setRoundOff] = useState("");

  const [items, setItems] = useState(() =>
    purchaseData.map((item) => ({
      ...item,
      location: 'I' || deptLocation,
    })),
  );
  const [initialRowUnitOptions, setInitialRowUnitOptions] = useState({});
  const [showSidebar, setShowSidebar] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(
    "I",
  );
  const [itemInfo, setItemInfo] = useState({
    qtyInhand: 0,
    costAvg: 0,
    lSalesprice: 0,
    lpCost: 0,
    lCSalesprice: 0,
    pSoQty: 36,
    pPoQty: "RRD",
  });

  const [otherCosts, setOtherCosts] = useState([]);
  const [isOtherCostEnabled, setIsOtherCostEnabled] = useState(false);
  const [supplierData, setSupplierData] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isModificationModalOpen, setIsModificationModalOpen] = useState(false);
  const [bomLoading, setBomLoading] = useState(false);
  const [unitCostManual, setUnitCostManual] = useState(false);
  const [savedInvoiceNumber, setSavedInvoiceNumber] = useState(null);
  const [isOtherCostModalOpen, setIsOtherCostModalOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState("");
  const [serviceResults, setServiceResults] = useState([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [serviceLoading, setServiceLoading] = useState(false);
  const isTemplateSelected = useRef(false);
  const [focusedProductCode, setFocusedProductCode] = useState(null);
  const [currentStockInhand, setCurrentStockInhand] = useState(null);

  // Document data
  const [documentData, setDocumentData] = useState({
    documentNo: "",
    date: todayDate,
    paymentMode: "cash",
    invNo: "",
    supplier: "",
    unregSuppl: "",
    trn: "",
    dueDays: "",
    dueDate: todayDate,
    cfAgent: "",
    shipRef: "",
    docDate: todayDate,
    jobCode: "",
    remarks: "",
    currency: "AED",
    currencyRate: "1",
    voucherType: "",
    creditAcc: "",
    debitAcc: "",
    invNumber: "",
    vatAcc: "",
    crAccount: "",
    drAccount: "",
    woNo: "",
    otherCostsTotal: "0",
    productionQty: "0",
    productCode: "",
    productDescription: "",
    unitCost: "0",
    location: 'K',
  });
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

  const fetchTransferDocument = async (docNo) => {
    try {
      const masterUrl = `${BASE_URL}/api/CRMDocTransfer/${COMPANY_CODE}/TRANSFER/PRODUCTIONMASTER/-/-/${DEPARTMENT}/${docNo}/-`;
      const detailUrl = `${BASE_URL}/api/CRMDocTransfer/${COMPANY_CODE}/TRANSFER/PRODUCTIONDETAIL/-/-/${DEPARTMENT}/${docNo}/-`;

      const [masterRes, detailRes] = await Promise.all([
        fetch(masterUrl),
        fetch(detailUrl),
      ]);

      const masterData = await masterRes.json();
      const detailData = await detailRes.json();

      if (masterData && masterData.length > 0) {
        const main = masterData[0];
        setDocumentData((prev) => ({
          ...prev,
          documentNo: String(main.inv_no || ""),
          date: main.date ? main.date.slice(0, 10) : prev.date,
          currency: main.fc || prev.currency,
          currencyRate: main.rate || prev.currencyRate,
          remarks: main.remarks || prev.remarks,
          jobCode: main.jobCode || prev.jobCode,
          crAccount: (main.account || main.acc || "")?.toString().trim(),
          drAccount: (main.purchAccount || main.purchAcc || "")
            ?.toString()
            .trim(),
          productCode: main.accdesc,
          productDescription: main.inv,
          productionQty: Number(main.remarks) || main.Pending_Qty || 0,
          shipRef: main.shipref || prev.shipRef,
          woNo: main.CCNO || prev.woNo,
          otherCostsTotal: main.othercost || "0",
          location: main.lcno || ""
        }));
        setDiscountAmount(main.discount ?? main.discount ?? 0);
        setRoundOff(main.rdoff ?? 0);
      }

      if (detailData && detailData.length > 0) {
        const mappedItems = detailData.map((r, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: r.code || "",
          description: r.idesc || r.Description || "",
          location: r.locn || "",
          unit: r.unit || "PCS",
          qty: Number(r.tr_qty2 ?? r.tr_qty ?? r.tr_qty2) || 0,
          unitCost: Number(r.unit_cost ?? r.unit_price ?? r.funit_cost ?? 0),
          amount: Number(r.line_total ?? r.fline_tota ?? 0),
          unitBCCost: Number(r.funit_cost ?? r.unit_cost ?? 0),
          fraction: r.fraction ?? 1,
          total: Number(r.line_total ?? r.fline_tota ?? 0),
          cntrl: r.soGuid,
          isActive: r.tr_type === "I" ? "No" : r.tr_type === "O" ? "Yes" : "Yes",
        }));
        setItems(mappedItems);
      }
    } catch (err) {
      console.error("Failed to fetch transfer document:", err);
      alert("Failed to load transferred production: " + err.message);
    }
  };

  const initializeDocument = useCallback(async () => {
    try {
      const { voucherType, documentNo } = await fetchDocumentNumber(
        documentType,
        documentData.paymentMode,
        BASE_URL,
        COMPANY_CODE,
        DEPARTMENT,
      );

      if (documentNo) {
        setDocumentData((prev) => ({
          ...prev,
          documentNo: documentNo,
          voucherType,
        }));
      }
      console.log("Voucher Type:", voucherType, documentNo);
    } catch (err) {
      console.error("Error initializing document:", err);
    }
  }, [
    documentType,
    documentData?.paymentMode,
    BASE_URL,
    COMPANY_CODE,
    DEPARTMENT,
  ]);

  useEffect(() => {
    const docNo = location?.state?.docNo;

    if (docNo && request) {
      fetchTransferDocument(docNo);
    }
  }, [location?.state?.docNo]);

  useEffect(() => {
    const workOrder = location?.state?.workOrder;
    console.log("Work Order from location.state:", workOrder);
    if (!workOrder) return;

    setDocumentData((prev) => ({
      ...prev,
      productCode:
        workOrder.productCode || workOrder.Product_Code || workOrder.code || "",
      productDescription:
        workOrder.description ||
        workOrder.Description ||
        workOrder.productDescription ||
        "",
      productionQty: workOrder.Pending_Qty || 0,
      woNo: workOrder.woNo || workOrder.Wo_No || prev.woNo || "",
      shipRef: workOrder.soUid || "",
    }));
  }, [location?.state?.workOrder]);
  const fetchBomItems = async (productCode) => {
    try {
      setBomLoading(true);
      const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/BOMITEMS/${encodeURIComponent(productCode)}/-`;
      // const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/BOMITEMS/1001/-`;

      const resp = await request({ url, method: "GET" });
      console.log("BOM Items:", resp);
      const qtyMultiplier =
        Number(documentData.productionQty) ||
        Number(location?.state?.workOrder?.pendingQty) ||
        1;

      if (resp && resp.length > 0) {
        const mapped = resp.map((r, idx) => {
          const perUnitQty = Number(r.QTY || 0) || 0;
          const qty = perUnitQty * qtyMultiplier;
          const unitCost = Number(r.Cost || 0) || 0;
          const amount = Number((qty * unitCost).toFixed(2));
          return {
            id: idx + 1,
            sino: idx + 1,
            code: (r.CODE || "").toString().trim(),
            description: r.DESCRIPTION || "",
            location:
              r.locn ||
              // localStorage.getItem("departmentdepartmentLocation") ||
              "I",
            unit: r.unit || "PCS",
            qty: r.QTY || 0,
            unitCost: r.Cost || 0,
            unitBCCost: r.Cost,
            amount,
            othCost: 0,
            x: r.x || "",
            cntrl: r.cntrl || "",
            fraction: 1,
            vatEnabled: true,
            vatPercent: VATPERCENTAGE || 0,
            vatAmount: 0,
            total: amount,
            mastInfo: "",
            pono: 0,
            oem: "",
            group: "",
            "Discount%": 0,
            git: "",
            salesperson:
              location?.state?.workOrder?.salesPerson || CURRENT_USER,
            bin: "",
            remarks: r.Remarks || r.remark || "",
            dcp: "",
            isActive: "Yes",
            dac: r.DAC ?? r.dac ?? "0",
            Br_Stk: Number(r.Br_Stk ?? r.br_stk ?? r.BrStk ?? 0),
            Br_Qty: Number(r.Br_Qty ?? r.br_qty ?? r.BrQty ?? 0),
          };
        });

        setItems(mapped);
      }
    } catch (err) {
      console.error("Failed to fetch BOM items:", err);
    } finally {
      setBomLoading(false);
    }
  };
  // Fetch BOM items when productCode is present
  useEffect(() => {
    const productCode = location?.state?.workOrder?.Product_Code || "";
    if (!productCode || !request) return;

    fetchBomItems(productCode);
  }, [location?.state?.workOrder]);

  useEffect(() => {
    if (mode === "new") {
      initializeDocument();
    }
  }, [mode, initializeDocument]);

  // VAT calculation
  const getVatPercent = (documentData) =>
    documentData.vatStatus === "VAT Applied" ? VATPERCENTAGE : 0;

  const calculateTotals = () => {
    const round = (val) => Math.round(Number(val) * 100) / 100;

    const processedItems = items.map((item) =>
      calculateRowValues(item, documentData, {
        items,
        otherCosts,
      }),
    );

    const rowTaxableSum = round(
      processedItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    );

    const rowVatSum = round(
      processedItems.reduce(
        (sum, item) => sum + (Number(item.vatAmount) || 0),
        0,
      ),
    );

    // Sum of other-cost rows (they have .total)
    const otherCostTotal = round(
      (otherCosts || []).reduce((sum, c) => sum + (Number(c.total) || 0), 0),
    );

    const grossAmount = round(
      processedItems.reduce((sum, item) => sum + (Number(item.total) || 0), 0),
    );

    const globalDiscount = Number(discountAmount) || 0;
    const roundOffValue = Number(roundOff) || 0;

    const taxableAmount = round(rowTaxableSum - globalDiscount - roundOffValue);

    const totalVat =
      !autoVatIsOn && vatAmountManual !== null
        ? round(vatAmountManual)
        : rowVatSum;

    const baseTotalAmount = round(taxableAmount + totalVat);

    // Add other-costs into final amount so unit cost accounts for them
    const totalAmountWithOther = round(baseTotalAmount + otherCostTotal);

    const netAmount = round(totalAmountWithOther);

    const currencyRate = Number(documentData.currencyRate) || 1;
    const bcAmt = round(netAmount * currencyRate);

    return {
      grossAmount: grossAmount.toFixed(2),
      taxableAmount: taxableAmount.toFixed(2),
      vatAmount: totalVat.toFixed(2),
      discountAmount: globalDiscount.toFixed(2),
      roundOff: roundOffValue.toFixed(2),
      netAmount: netAmount.toFixed(2),
      otherCharges: otherCostTotal.toFixed(2),
      bcAmt: bcAmt.toFixed(2),
    };
  };

  const totals = calculateTotals();

  // Auto-calc unit cost from netAmount / productionQty
  useEffect(() => {
    const qty = Number(documentData?.productionQty) || 0;
    const netAmount = Number(totals?.netAmount) || 0;

    let unitCost = 0;

    if (qty > 0) {
      // totals.netAmount already includes otherCostsTotal, so we just divide by qty
      unitCost = Math.round((netAmount / qty) * 100) / 100;
    }

    setDocumentData((prev) => {
      const prevUnit = Number(prev.unitCost) || 0;
      if (prevUnit !== unitCost) {
        return { ...prev, unitCost };
      }
      return prev;
    });
  }, [totals?.netAmount, documentData?.productionQty]);

  useEffect(() => {
    const otherTotal = (otherCosts || []).reduce(
      (s, c) => s + (Number(c.total) || 0),
      0,
    );
    setDocumentData((prev) => ({
      ...prev,
      otherCostsTotal: otherTotal.toFixed(2),
    }));
  }, [otherCosts]);
  const handleServiceSelect = async (service) => {
    try {
      const productCode = service.code;
      const description = service.Description;

      isTemplateSelected.current = true;
      setServiceSearch(productCode);
      setShowServiceDropdown(false);

      // setDocumentData((prev) => ({
      //   ...prev,
      //   productCode,
      //   productDescription: description,
      // }));

      await fetchBomItems(productCode);
    } catch (err) {
      console.error("Service selection failed:", err);
    }
  };
  const searchServiceItems = async (searchText) => {
    if (!searchText) {
      setServiceResults([]);
      return;
    }

    try {
      setServiceLoading(true);

      const url = `${BASE_URL}/api/cpayscount/${COMPANY_CODE}/BOQTEMPLATE/${encodeURIComponent(searchText)}/-`;
      const resp = await request({
        url,
        method: "GET",
      });

      if (resp && resp.length > 0) {
        setServiceResults(resp);
        setShowServiceDropdown(true);
      } else {
        setServiceResults([]);
        setShowServiceDropdown(false);
      }
    } catch (err) {
      console.error("Service search failed:", err);
    } finally {
      setServiceLoading(false);
    }
  };
  useEffect(() => {
    if (!serviceSearch) {
      setServiceResults([]);
      setShowServiceDropdown(false);
      return;
    }

    if (isTemplateSelected.current) {
      isTemplateSelected.current = false;
      return;
    }

    const delay = setTimeout(() => {
      searchServiceItems(serviceSearch);
    }, 400);

    return () => clearTimeout(delay);
  }, [serviceSearch]);

  const refreshStockDocument = async (
    companyCode,
    documentNumber,
    department
  ) => {
    try {
      const url = `${BASE_URL}/api/CRMStockRefreshDoc/${companyCode}/PROD/${documentNumber}/${department}`;
      console.log("Calling refreshStockDocument URL:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const refreshResult = await response.json();
      console.log("Stock refresh response:", refreshResult);

      if (!response.ok) {
        throw new Error(`Stock refresh failed with status: ${response.status}`);
      }

      return refreshResult;
    } catch (error) {
      console.error("Error in refreshStockDocument:", error);
      // We don't necessarily want to throw here to prevent the whole save flow from being "failed"
      // if the document was already saved successfully.
      return { error: error.message };
    }
  };
  const handleSave = async () => {
    const productCode = documentData.productCode?.toString()?.trim();
    const productDesc = documentData.productDescription?.toString()?.trim();
    const productionQty = Number(documentData.productionQty || 0);

    if (!productCode) {
      alert("Product code is required to save production.");
      return;
    }
    if (!productionQty || productionQty <= 0) {
      alert("Production quantity must be greater than zero.");
      return;
    }
    if (
      items.filter((item) => item.code && item.code.toString().trim() !== "")
        .length === 0
    ) {
      alert("Please add at least one item before saving.");
      return;
    }

    // ── Save-time stock validation ───────────────────────────────────────────
    const isBlocked = checkIfUserHasAccess("SX");
    const activeItemsForValidation = items.filter(
      (item) => item.code && item.code.toString().trim() !== "",
    );
    const overStockItems = activeItemsForValidation.filter((item) => {
      // Skip service items
      if (item.dac === "1" || item.dac === true || item.DAC === true) return false;
      // Skip inactive items
      if (String(item.isActive || "").toLowerCase() === "no") return false;
      const available = Number(item.Br_Stk ?? item.Br_Qty ?? 0);
      const requested = Number(item.qty ?? 0);
      return available > 0 && requested > available;
    });

    if (overStockItems.length > 0) {
      if (isBlocked) {
        const adjusted = items.map((item) => {
          // Skip inactive items
          if (String(item.isActive || "").toLowerCase() === "no") return item;
          const available = Number(item.Br_Stk ?? item.Br_Qty ?? 0);
          const requested = Number(item.qty ?? 0);
          if (requested > available) {
            return { ...item, qty: available > 0 ? available : 0 };
          }
          return item;
        });
        setItems(adjusted);
        alert(
          `Qty exceeded available stock for one or more items. They have been adjusted to available stock.`,
        );
        return;
      } else {
        const itemList = overStockItems
          .map(
            (it) =>
              `• ${it.code} — requested: ${it.qty}, available: ${Number(it.Br_Stk ?? it.Br_Qty ?? 0)}`,
          )
          .join("\n");
        const confirmed = window.confirm(
          `The following items exceed available stock:\n${itemList}\n\nDo you want to save anyway with minus quantity?`,
        );
        if (!confirmed) return;
      }
    }
    // ────────────────────────────────────────────────────────────────────────

    setIsSaving(true);

    try {
      const calculatedItems = items.map((item) =>
        calculateRowValues(item, documentData, { items, otherCosts }),
      );

      const headerProductCode = productCode;
      const headerProductDesc =
        productDesc || calculatedItems[0]?.description || "";
      const headerRemarks = String(productionQty);

      const payload = calculatedItems
        .filter((item) => item.code && item.code.toString().trim() !== "")
        .map((item, idx) => {
          const qty = item.qty?.toString() || "0";
          const unitCost = (Number(item.unitCost) || 0).toString();
          const unitBCCost = (item.unitBCCost ?? item.unitCost ?? 0).toString();
          const amountLine = (Number(item.amount) || 0).toFixed(2);
          const vatAmtLine = (Number(item.vatAmount) || 0).toFixed(2);
          const totalLine = (Number(item.total) || 0).toFixed(2);

          return {
            cmpcode: COMPANY_CODE,
            modeop: documentNoFromState ? "EDIT" : "SAVE",
            inv_no: documentData.documentNo || "0000",
            ref: documentData.invNo || "",
            fc: documentData.currency || "AED",
            amount: totals.netAmount || "0",
            fcamount: totals.netAmount || "0",
            account: documentData.crAccount || "",
            accdesc: headerProductCode,
            status: "0",
            remarks: headerRemarks,
            deptno: DEPARTMENT,
            date: documentData.date || todayDate,
            inv: headerProductDesc || documentData.invNumber || "",
            shipDate: documentData.docDate || todayDate,
            shipref: documentData.shipRef || "",
            duedate: documentData.dueDate || todayDate,
            mawb: "",
            hwab: "",
            lcno: documentData.location || "",
            docref: "",
            cfagent: documentData.cfAgent || "",
            purchAccount: documentData.drAccount || "",
            purchAccDesc: CURRENT_USER,
            rate: documentData.currencyRate || "1",
            local: documentData.purchaseType === "Local" ? "0" : "1",
            othercost: documentData.otherCostsTotal || "0",
            jobCode: documentData.jobCode || "",
            discount: Number(discountAmount) || "0",
            fdiscount: documentData.unitCost || '0',
            CCNO: documentData?.woNo || "",
            SERIALNO: "0",
            vatamt: totals.vatAmount || "0",
            rdoff: roundOff?.toString() || "0",
            costUpd: "NEW",
            docinfo: "",
            mrv_no: "0",
            p1: "0",
            p2: "0",
            vatacc: documentData.vatAcc || "",
            vataccImp: documentData.vatAccImp || "",
            method: documentData.paymentMode || "cash",
            cashcred: documentData.paymentMode === "cash" ? "CASH_PAID" : "",
            cashAcc: documentData.creditAcc || supplierData?.ACCOUNT || "",
            vatType: documentData.vatStatus || "VAT Applied",
            Slno: item.isActive === "Yes" ? "Y" : "N",
            code: item.code || "",
            description: item.description || "",
            locn: item.location || "",
            unit: item.unit || "",
            qty: qty,
            "Unit Cost": unitCost,
            "Unit BCCost": unitBCCost,
            AmountLine: amountLine,
            "Oth.Cost": (Number(item.othCost) || 0).toFixed(2),
            x: item.x || "",
            cntrl: item.cntrl || "",
            frac: item.fraction || 1,
            "vat%": (item.vatPercent || 0).toString(),
            VatamtLine: vatAmtLine,
            Mastinfo: item.mastInfo || "",
            pono: item.pono || "0",
            total: totalLine,
            oem: item.oem || "",
            group: item.group || "",
            "disc%": (Number(item["Discount%"]) || 0).toString(),
            git: item.git || "0",
            Salesperson: item.salesperson || "",
            bin: item.bin || "",
            RemarksLine: item.remarks || "",
            DCP: item.dcp || "0",
          };
        });

      console.log("Payload to save:", payload);
      // return
      const response = await fetch(`${BASE_URL}${config.apiEndpoints.save}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("Save response:", result);

      if (response?.status === 200) {

        const invoiceNumber = mode === "edit" || documentNoFromState
          ? documentData.documentNo
          : result?.invoiceNo;
        await refreshStockDocument(COMPANY_CODE, invoiceNumber, DEPARTMENT);
        // Store the saved invoice number
        setSavedInvoiceNumber(invoiceNumber);

        // Update document data with the saved invoice number
        setDocumentData((prev) => ({
          ...prev,
          documentNo: invoiceNumber,
        }));

        alert(`${config.title} no: ${invoiceNumber} saved successfully.`);

        // Production.js (inside if response?.status === 200)
        if (otherCosts && otherCosts.length > 0) {
          const hasValidOtherCosts = otherCosts.some(
            (cost) => cost.empCode && cost.empCode.trim() !== "",
          );

          if (hasValidOtherCosts) {
            const wagePayload = otherCosts
              .filter((r) => r.empCode && r.empCode.trim() !== "")
              .map((r) => ({
                CmpCode: COMPANY_CODE,
                mode: "ENTRY",
                inv_no: Number(invoiceNumber), // IMPORTANT: use invoice number from response
                deptno: DEPARTMENT,
                empno: r.empCode,
                empname: r.empName,
                pdate: r.pdate || documentData.date || todayDate,
                jobno: r.jobno || documentData.jobCode || "",
                nr_hrs: Number(r.nrHrs || 0),
                nr_rate: Number(r.nrRate || 0),
                tot_wage: Number(r.total || 0),
              }));

            try {
              const wageRes = await fetch(`${BASE_URL}/api/CRMProdWageReg`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(wagePayload),
              });

              if (!wageRes.ok) {
                const errText = await wageRes.text();
                console.warn("Wage save failed:", errText);
                alert("Production saved but failed to save other costs.");
              }
            } catch (err) {
              console.error("Error saving wages:", err);
              alert("Production saved but failed to save other costs.");
            }
          }
        }

        navigate("/production_dashboard");
      } else {
        console.error("Save failed:", result);
        alert(`Failed to save ${config.title}.`);
      }
    } catch (err) {
      console.error("Error saving:", err);
      alert(`Error saving ${config.title}.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = () => {
    navigate("/production_dashboard");
  };

  const handleParentTabChange = (event, newValue) => {
    setParentTabValue(newValue);
  };

  return (
    <div className="w-full relative">
      <div className="flex items-center justify-between lg:px-10 px-2 mb-2 gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">{config.title}</h1>
        </div>

        <Tooltip title={tooltipContent} sx={{ margin: 0, padding: 0 }}>
          <IconButton>
            <HelpOutline
              className="text-blue-600 bg-white rounded-full"
              fontSize="medium"
            />
          </IconButton>
        </Tooltip>
      </div>

      <ModeIndicator mode={mode} documentType={documentType} />

      <div className="w-full rounded-lg">
        <div className="lg:px-10">
          <div className="bg-gray-50 border lg:p-3 rounded-lg shadow pb-6">
            <ThemeProvider theme={theme}>
              <TabContext value={parentTabValue}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TabList onChange={handleParentTabChange}>
                    <Tab label="General" value="1" sx={{ padding: 0 }} />
                    <Tab label="Timeline" value="3" sx={{ padding: 0 }} />
                  </TabList>
                  <div className="lg:flex hidden items-center gap-3">
                    <div className="relative w-64">
                      <input
                        type="text"
                        placeholder="Search Template..."
                        value={serviceSearch}
                        onChange={(e) => {
                          setServiceSearch(e.target.value);
                          setShowServiceDropdown(true);
                        }}
                        onBlur={() => setShowServiceDropdown(false)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors"
                      />

                      {serviceLoading && (
                        <Loader className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-gray-500" />
                      )}

                      {showServiceDropdown && serviceResults.length > 0 && (
                        <div className="absolute z-50 top-full mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg w-full max-h-60 overflow-y-auto">
                          {serviceResults.map((item, idx) => (
                            <div
                              key={idx}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleServiceSelect(item)}
                              className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b last:border-b-0 transition-colors"
                            >
                              <div className="font-semibold text-gray-800">
                                {item.code}
                              </div>
                              <div
                                className="text-gray-500 text-xs mt-0.5 truncate"
                                title={item.Description}
                              >
                                {item.Description}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-2 px-5 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg shadow hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <View className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        if (mode === "edit") {
                          setIsModificationModalOpen(true);
                        } else {
                          handleSave();
                        }
                      }}
                      disabled={mode === "view" || isSaving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {mode === "edit" ? "Update" : "Save"}
                        </>
                      )}
                    </button>
                  </div>
                </Box>
                <TabPanel value="1" sx={{ p: 0, paddingX: 0 }}>
                  <ProductionGeneral
                    productionData={documentData}
                    setPurchaseInvoiceData={setDocumentData}
                    COMPANY_CODE={COMPANY_CODE}
                    DEPARTMENT={DEPARTMENT}
                    BASE_URL={BASE_URL}
                    setSupplierData={setSupplierData}
                    selectedSupplier={selectedSupplier}
                    setSelectedSupplier={setSelectedSupplier}
                    supplierData={supplierData}
                    documentType="PRODUCTION"
                    unitCostManual={unitCostManual}
                    setUnitCostManual={setUnitCostManual}
                    otherCosts={otherCosts}
                    setOtherCosts={setOtherCosts}
                    mode={mode}
                    setIsOtherCostModalOpen={setIsOtherCostModalOpen}
                    isOtherCostModalOpen={isOtherCostModalOpen}
                    currency={documentData.currency}
                    currencyRate={documentData.currencyRate}
                  />
                </TabPanel>
                <TabPanel value="3" sx={{ p: 1 }}>
                  <TimelineTab
                    BASE_URL={BASE_URL}
                    DEPARTMENT={DEPARTMENT}
                    modificationType={MODIFICATION_TYPES[documentType] || null}
                    invoiceId={documentNoFromState}
                    COMPANY_CODE={COMPANY_CODE}
                  />
                </TabPanel>
              </TabContext>
            </ThemeProvider>
          </div>
        </div>

        <main className="w-full mt-4 relative">
          <ProductionTable
            items={items}
            onItemsChange={setItems}
            setShowSidebar={setShowSidebar}
            setItemInfo={setItemInfo}
            setCurrentLocation={setCurrentLocation}
            extraFields={[]}
            purchaseData={documentData}
            initialRowUnitOptions={initialRowUnitOptions}
            setInitialRowUnitOptions={setInitialRowUnitOptions}
            isOtherCostEnabled={isOtherCostEnabled}
            setIsOtherCostEnabled={setIsOtherCostEnabled}
            otherCosts={otherCosts}
            setOtherCosts={setOtherCosts}
            calculateRowValues={calculateRowValues}
            invoiceData={documentData}
            getVatPercent={getVatPercent}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            DEPARTMENT_NO={DEPARTMENT}
            hideAddButton={true}
            setFocusedProductCode={setFocusedProductCode}
            currentStockInhand={currentStockInhand}
            documentType={documentType}
          />


          {checkIfUserHasAccess("FT") && (
            <FloatingItemInformation
              showSidebar={showSidebar}
              currentLocation={currentLocation}
              companyCode={COMPANY_CODE}
              API_BASE_URL={BASE_URL}
              productCode={focusedProductCode}
              department={DEPARTMENT}
              onStockInhandUpdate={setCurrentStockInhand}
            />
          )}
        </main>
      </div>
      {/* Hide Net Amount if useraccess contains AC, SW, or ST */}
      {(() => {
        let userAccess = "";
        try {
          const userObj = JSON.parse(localStorage.getItem("currentUser"));
          userAccess = userObj?.[0]?.useraccess || "";
        } catch (e) { }
        const shouldHideCost = ["-AC", "-SW", "-ST"].some((code) => userAccess.includes(code));
        if (shouldHideCost) return null;
        return (
          <div className="w-full lg:px-10 p-1 mt-2 grid grid-cols-4">
            <div className=" col-span-3">
              <div className="p-4 bg-white shadow border border-purple-200 rounded-lg text-center">
                <div className="text-sm text-gray-600 mb-1">Net Amount</div>
                <div className="text-2xl font-bold text-blue-600">
                  {totals.netAmount}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <Modal
        isOpen={isModificationModalOpen}
        onClose={() => setIsModificationModalOpen(false)}
        size="md"
        header={true}
        title="Update Confirmation"
        showFullScreen={false}
      >
        <ModificationCommentModal
          isOpen={isModificationModalOpen}
          onClose={() => setIsModificationModalOpen(false)}
          handleSave={handleSave}
          documentData={documentData}
          documentType={documentType}
          COMPANY_CODE={COMPANY_CODE}
          DEPARTMENT={DEPARTMENT}
          CURRENT_USER={CURRENT_USER}
          BASE_URL={BASE_URL}
          setIsModificationModalOpen={setIsModificationModalOpen}
        />
      </Modal>
    </div>
  );
};

export default Production;
